import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import AgentsService from 'services/Agents.service';

export const AuthenticatedUserContext = createContext();

const AuthenticatedUserProvider = ({ children }) => {
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasAuthError = useRef(false);

  const fetchAuthenticatedUser = useCallback(async () => {
    // Se já detectou erro 401, não tenta mais fazer requisições
    if (hasAuthError.current) {
      setIsLoading(false);
      return;
    }

    // Verificar se o token existe antes de fazer a requisição
    const token = Cookies.get('token');
    if (!token) {
      setIsLoading(false);
      setAuthenticatedUser(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await AgentsService.getAuthenticatedUser();

      // Verificar se a resposta é um Response com status 401 (token expirado)
      if (response instanceof Response && response.status === 401) {
        hasAuthError.current = true;
        Cookies.remove('token');
        setAuthenticatedUser(null);
        setIsLoading(false);
        // O redirecionamento será feito pelo HttpMethods.js
        return;
      }

      if (response instanceof Response) {
        throw new Error('Failed to get authenticated user');
      }

      if (response && response.id) {
        setAuthenticatedUser(response);
        // Reset do flag de erro quando autenticação é bem-sucedida
        hasAuthError.current = false;
      } else {
        throw new Error('Invalid user data received');
      }
    } catch (err) {
      console.error('Error fetching authenticated user:', err);
      setError(err.message || 'Failed to load user data');
      setAuthenticatedUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuthenticatedUser();
  }, [fetchAuthenticatedUser]);

  const refreshUser = useCallback(async () => {
    // Se já detectou erro 401, não tenta mais fazer requisições
    if (hasAuthError.current) {
      throw new Error('Unauthorized - token expired');
    }

    // Verificar se o token existe antes de fazer a requisição
    const token = Cookies.get('token');
    if (!token) {
      setAuthenticatedUser(null);
      throw new Error('No token available');
    }

    try {
      setError(null);
      const response = await AgentsService.getAuthenticatedUser();

      // Verificar se a resposta é um Response com status 401 (token expirado)
      if (response instanceof Response && response.status === 401) {
        hasAuthError.current = true;
        Cookies.remove('token');
        setAuthenticatedUser(null);
        // O redirecionamento será feito pelo HttpMethods.js
        throw new Error('Unauthorized - token expired');
      }

      if (response instanceof Response) {
        throw new Error('Failed to get authenticated user');
      }

      if (response && response.id) {
        setAuthenticatedUser(response);
        // Reset do flag de erro quando autenticação é bem-sucedida
        hasAuthError.current = false;
      } else {
        throw new Error('Invalid user data received');
      }
    } catch (err) {
      console.error('Error refreshing authenticated user:', err);
      setError(err.message || 'Failed to refresh user data');
      throw err;
    }
  }, []);

  return (
    <AuthenticatedUserContext.Provider
      value={{ authenticatedUser, isLoading, error, refreshUser }}
    >
      {children}
    </AuthenticatedUserContext.Provider>
  );
};

AuthenticatedUserProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthenticatedUserProvider;
